import { useId, type TextareaHTMLAttributes } from 'react';

import styles from './TextArea.module.css';

export interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label: string;
  hint?: string;
  error?: string;
  /** `mono` for anything the reader is reading as code — a template body, a
   *  JSON bag — where proportional type hides the whitespace that matters. */
  face?: 'sans' | 'mono';
}

/** `TextField`'s multi-line twin, down to the label/hint/error wiring. */
export function TextArea({ label, hint, error, face = 'sans', ...rest }: TextAreaProps) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <textarea
        {...rest}
        data-1p-ignore
        id={id}
        className={`${styles.input} ${styles[face]}`}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
      />
      {error ? (
        <p className={styles.error} id={`${id}-error`} role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className={styles.hint} id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
